import { RRule, rrulestr } from "rrule";
import { toDate } from "date-fns-tz";
import { startOfMonth } from "date-fns";

export function getNthOccurrenceOfDayInMonth(date: Date): number {
  if (!date || Number.isNaN(new Date(date).getTime())) {
    return 0;
  }

  const currentDate = toDate(date);
  const dayOfMonth = currentDate.getDate();
  const targetDay = currentDate.getDay();

  const startOfMonthDate = startOfMonth(currentDate);

  // Example: Find the first Sunday in February 2025 (Month starts on Saturday)
  // targetDay - startOfMonthDate.getDay() = 0 - 6 = -6
  // Add 7 to avoid negatives: (-6 + 7) = 1
  // Modulo to avoid leaving 0-6 range: 1 % 7 = 1
  // Result: The first Sunday is on the 2nd day of the month (1 + 1 = 2).
  const firstTargetDay =
    startOfMonthDate.getDate() +
    ((targetDay - startOfMonthDate.getDay() + 7) % 7);

  return Math.ceil((dayOfMonth - firstTargetDay + 1) / 7);
}

export const RRULE_WEEKDAYS = [
  RRule.SU,
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
  RRule.SA
];
function getDayWeekNumber(date: Date, nth: number) {
  const weekDays = RRULE_WEEKDAYS[date.getDay()];
  return weekDays.nth(nth);
}

export const byWeekDay = (date: Date) => {
  return [getDayWeekNumber(date, getNthOccurrenceOfDayInMonth(date))];
};

/**
 * Sanitizes an RRULE string by removing invalid properties like BYNWEEKDAY
 * @param rruleString - The RRULE string to sanitize
 * @returns The sanitized RRULE string
 */
export function sanitizeRRuleString(rruleString: string): string {
  let sanitized = rruleString;
  // Remove BYNWEEKDAY if it exists as it might cause parsing issues
  sanitized = sanitized.replace(/BYNWEEKDAY=[^;]+;?/g, "");
  // Clean up any double semicolons that might result
  sanitized = sanitized.replace(/;;+/g, ";");
  // Remove trailing semicolon
  sanitized = sanitized.replace(/;+$/, "");
  return sanitized;
}

/**
 * Parses an RRULE string and returns a clean RRule object
 * Sanitizes the input to remove invalid properties like BYNWEEKDAY
 * @param rruleString - The RRULE string to parse (can be empty or "no")
 * @param dtstart - Optional start date to set on the parsed rule
 * @returns A parsed RRule object, or a default daily rule if parsing fails
 */
export function parseRRuleString(
  rruleString: string | "" | "no",
  dtstart?: Date
): RRule {
  if (rruleString === "" || rruleString === "no") {
    return new RRule({
      freq: RRule.DAILY,
      dtstart: dtstart
    });
  }

  try {
    // Sanitize the RRULE string to remove potentially problematic properties
    const sanitizedValue = sanitizeRRuleString(rruleString);

    const rule = rrulestr(sanitizedValue);
    // Update dtstart if date is available
    if (dtstart) {
      rule.options.dtstart = dtstart;
    }
    return rule;
  } catch (error) {
    console.warn("Failed to parse RRULE:", rruleString, error);
    // Fallback to a default RRule if parsing fails
    return new RRule({
      freq: RRule.DAILY,
      interval: 1,
      dtstart: dtstart
    });
  }
}

/**
 * Gets a clean RRULE string from a parsed rule, ensuring no invalid properties
 * Uses origOptions when available to avoid invalid computed properties
 * @param parsedRule - The parsed RRule object
 * @returns A clean RRULE string without invalid properties like BYNWEEKDAY
 */
export function getCleanRRuleString(parsedRule: RRule): string {
  return parsedRule.toString();
}

/**
 * Creates a clean RRULE string from a fixed value rule with an optional until date
 * Uses origOptions instead of options to avoid invalid computed properties
 * like bynweekday, byhour, byminute, bysecond that get added to options
 * @param fixedRule - The fixed value RRule object
 * @param untilDate - Optional until date to set on the rule
 * @returns A clean RRULE string without invalid properties like BYNWEEKDAY
 */
export function createCleanRRuleFromFixedValue(
  fixedRule: RRule,
  untilDate: Date | null = null
): string {
  // Use origOptions instead of options to avoid invalid computed properties
  // like bynweekday, byhour, byminute, bysecond that get added to options
  const optionsToUse = {
    ...fixedRule.origOptions,
    until: untilDate
  };

  const rrule = new RRule(optionsToUse);
  return rrule.toString();
}
